import { Component, computed, OnDestroy, OnInit, Signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LabelComponent } from '../../../../../common/label/label.component';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { actTypesByServiceUrl, ApplicationDataForm } from './forms/application-data.form';
import {
  ApplicationAppEntityApplicationRead,
  DocumentReceiptMethodAppEntityDictionaryDocumentReceiptMethod,
  DocumentReceiptMethodService
} from '../../../../../openapi';
import { selectApplication, selectLoading, selectPending } from '../../../../../store/selectors/application.selectors';
import { Subscription } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { updateApplication } from '../../../../../store/actions/application.actions';
import { NgxMaskDirective } from 'ngx-mask';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { UnrelatedDeviceFormComponent } from './components/unrelated-device-form/unrelated-device-form.component';
import { ApplicationSubjectLabelPipe } from '../../../../../pipes/application-subject-label.pipe';
import { ApplicationSubjectPlaceholderPipe } from '../../../../../pipes/application-subject-placeholder.pipe';
import { AlertComponent } from '../../../../../common/alert/alert.component';
import { CorrespondenceFormComponent } from './components/correspondence-form/correspondence-form.component';
import {
  DocumentIssuePurposeLabelPipe
} from '../../../../../pipes/document-issue-purpose/document-issue-purpose-label.pipe';
import {
  DocumentIssuePurposePlaceholderPipe
} from '../../../../../pipes/document-issue-purpose/document-issue-purpose-placeholder.pipe';

export interface ApplicationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addResponsiblePersonControls?: boolean;
  addCommentsControl?: boolean;
  addManagerControls?: boolean;
  addApplicationSubjectControl?: boolean;
  applicationSubjectControlType?: 'text' | 'select' | 'label';
  applicationSubjectOptions?: string[];
  applicationSubjectDefaultValue?: string;
  addUnrelatedDeviceForm?: boolean;
  addDeviceTypeControl?: boolean;
  addLegalInterestControl?: boolean;
  addActTypeControl?: boolean;
  defaultActTypeControlValue?: string;
  addApplicationConcernsStreetControl?: boolean;
  hideDocumentReceiptMethodControl?: boolean;
  addIssuedDocumentTypeControl?: boolean;
  addDocumentIssuePurposeControl?: boolean;
  placeOfFillingDeclarationInfo?: boolean;
}

@Component({
  selector: 'app-application-data-section',
  standalone: true,
  imports: [
    LabelComponent,
    CommonModule,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    SpinnerComponent,
    NgxMaskDirective,
    StagesComponent,
    UnrelatedDeviceFormComponent,
    ApplicationSubjectLabelPipe,
    ApplicationSubjectPlaceholderPipe,
    AlertComponent,
    CorrespondenceFormComponent,
    DocumentIssuePurposeLabelPipe,
    DocumentIssuePurposePlaceholderPipe
  ],
  providers: [
    ApplicationDataForm
  ],
  templateUrl: './application-data-section.component.html'
})
export class ApplicationDataSectionComponent implements OnInit, OnDestroy {

  public stages: string[] = this.route.snapshot.data['stages'] || [];

  public applicationSubjectControlType: 'text' | 'select' | 'label' = this.route.snapshot.data['applicationSubjectControlType'] || 'text';

  public defaultActTypeControlValue: string | undefined = this.route.snapshot.data['defaultActTypeControlValue'];

  public placeOfFillingDeclarationInfo: boolean = !!this.route.snapshot.data['placeOfFillingDeclarationInfo'];

  public applicationId: string = this.route.snapshot.params['appId'];

  public successUrl: string = this.route.snapshot.data['successUrl'];

  public cancelUrl: string = this.route.snapshot.data['cancelUrl'];

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  private applicationSubjectDefaultValue: string | undefined = this.route.snapshot.data['applicationSubjectDefaultValue'];

  private fetchApplication: Subscription = toObservable(this.application).subscribe(app => {
    if (app) {
      this.form.patchFormValues({
        ...app,
        applicationSubject: this.applicationSubjectDefaultValue || app?.applicationSubject,
        actType: this.defaultActTypeControlValue || app?.actType
      });
      if (this.defaultActTypeControlValue) {
        this.form.actType?.disable({ emitEvent: false });
      }
    }
  });

  private methods = toSignal(this.documentReceiptMethodService.getDocumentReceiptMethodCollectionDocumentReceiptMethodCollection({
    pagination: false
  }));

  public receiptMethods: Signal<DocumentReceiptMethodAppEntityDictionaryDocumentReceiptMethod[] | undefined> = computed(() => {
    const application = this.application();
    const methods = this.methods();
    if (!!application && !!methods?.length) {
      return application?.isFiledByProxy ? methods : methods.filter(v => !['0e62487f-24c1-4b5f-8a89-9f9129b3d727', 'cf926de7-379a-42fa-a4ec-7b398c984b30'].includes(v?.id || ''));
    } else {
      return [];
    }
  });

  public issuedDocumentTypes: string[] = [
    'Wypis',
    'Wyrys',
    'Wypis i Wyrys'
  ];

  public applicationSubjectOptions: string[] = this.route.snapshot.data['applicationSubjectOptions'] || ['Lokalizacji zjazdu', 'Przebudowy zjazdu'];

  public actTypes: Signal<string[]> = computed(() => {
    return actTypesByServiceUrl(this.application()?.service?.url || '');
  });

  constructor(
    public form: ApplicationDataForm,
    private documentReceiptMethodService: DocumentReceiptMethodService,
    private store$: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public submitAndGoNext(): void {
    if (this.form.valid) {
      this.store$.dispatch(updateApplication({
        id: this.applicationId || '',
        application: this.form.toWriteModel(),
        successFnc: (applicationId: string) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    if (!!this.route.snapshot.data['addResponsiblePersonControls']) {
      this.form.createResponsiblePersonControls();
    }
    if (!!this.route.snapshot.data['addCommentsControl']) {
      this.form.createCommentsControl();
    }
    if (!!this.route.snapshot.data['addManagerControls']) {
      this.form.createManagerControls();
    }
    if (!!this.route.snapshot.data['addUnrelatedDeviceForm']) {
      this.form.createUnrelatedDeviceForm();
    }
    if (!!this.route.snapshot.data['addApplicationSubjectControl']) {
      this.form.createApplicationSubjectControl();
    }
    if (!!this.route.snapshot.data['applicationSubjectDefaultValue']) {
      this.form.applicationSubject?.disable();
    }
    if (!!this.route.snapshot.data['addDeviceTypeControl']) {
      this.form.createDeviceTypeControl();
    }
    if (!!this.route.snapshot.data['addLegalInterestControl']) {
      this.form.createLegalInterestControl();
    }
    if (!!this.route.snapshot.data['addActTypeControl']) {
      this.form.createActTypeControl();
    }
    if (!!this.route.snapshot.data['addApplicationConcernsStreetControl']) {
      this.form.createApplicationConcernsStreetControl();
    }
    if (!!this.route.snapshot.data['hideDocumentReceiptMethodControl']) {
      this.form.removeControl('documentReceiptMethod');
    }
    if (!!this.route.snapshot.data['addIssuedDocumentTypeControl']) {
      this.form.createIssuedDocumentTypeControl();
    }
    if (!!this.route.snapshot.data['addDocumentIssuePurposeControl']) {
      this.form.createDocumentIssuePurposeControl();
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }
}
